import React, { useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Tooltip,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { formatDuration } from '../../utils/timeUtils';
import { getTextColorForBackground } from '../../utils/colorUtils';

const PointageStats = ({ releveRates, hoursPerCode, releveCodes, onShowWarning }) => {
  const previousValues = useRef({
    overflowingCodes: [],
    yearlyOverflowingCodes: []
  });

  useEffect(() => {
    // Vérifier les dépassements mensuels
    const overflowingCodes = hoursPerCode.codeDetails.filter(({ code, hoursLogged, hoursExpected, periodicite }) => 
      periodicite !== 'variable' && hoursLogged > hoursExpected
    );

    // Vérifier les dépassements annuels (pour annual et calendar)
    const yearlyOverflowingCodes = hoursPerCode.codeDetails.filter(({ code, yearHoursLogged, yearHoursExpected, periodicite }) => 
      (periodicite === 'annual' || periodicite === 'calendar') && 
      yearHoursExpected && yearHoursLogged > yearHoursExpected
    );

    // Vérifier si les dépassements ont changé
    const hasMonthlyChanged = JSON.stringify(overflowingCodes) !== JSON.stringify(previousValues.current.overflowingCodes);
    const hasYearlyChanged = JSON.stringify(yearlyOverflowingCodes) !== JSON.stringify(previousValues.current.yearlyOverflowingCodes);

    if ((overflowingCodes.length > 0 || yearlyOverflowingCodes.length > 0) && (hasMonthlyChanged || hasYearlyChanged)) {
      const messages = [];
      
      if (overflowingCodes.length > 0) {
        const monthlyMessages = overflowingCodes.map(({ code, hoursLogged, hoursExpected }) => 
          `${code}: ${formatDuration(hoursLogged)} / ${formatDuration(hoursExpected)} (mensuel)`
        );
        messages.push(...monthlyMessages);
      }

      if (yearlyOverflowingCodes.length > 0) {
        const yearlyMessages = yearlyOverflowingCodes.map(({ code, yearHoursLogged, yearHoursExpected, periodicite }) => 
          `${code}: ${formatDuration(yearHoursLogged)} / ${formatDuration(yearHoursExpected)} (${periodicite === 'calendar' ? 'année calendaire' : 'annuel'})`
        );
        messages.push(...yearlyMessages);
      }

      onShowWarning(`Dépassement de dotation pour : ${messages.join(', ')}`);
    }

    // Mettre à jour les valeurs précédentes
    previousValues.current.overflowingCodes = overflowingCodes;
    previousValues.current.yearlyOverflowingCodes = yearlyOverflowingCodes;
  }, [hoursPerCode, onShowWarning]);

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={4}>
        <Typography variant="subtitle1" gutterBottom>Taux de relève</Typography>
        <Box display="flex" gap={4}>
          {/* Stats mensuelles */}
          <Box>
            <Typography variant="body2" color="text.secondary">Mois</Typography>
            <Typography variant="h4" color="primary" sx={{ mb: 0.5 }}>
              {releveRates.monthly?.rate ?? 0}%
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {releveRates.monthly?.includedDays ?? 0} / {releveRates.monthly?.totalWorkDays ?? 0} jours
              {releveRates.monthly?.excludedDays > 0 && ` (${releveRates.monthly.excludedDays} exclus)`}
            </Typography>
          </Box>
          {/* Stats sur 12 mois */}
          <Box>
            <Typography variant="body2" color="text.secondary">12 mois</Typography>
            <Typography variant="h4" color="primary" sx={{ mb: 0.5 }}>
              {releveRates.rolling?.rate ?? 0}%
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {releveRates.rolling?.includedDays ?? 0} / {releveRates.rolling?.totalWorkDays ?? 0} jours
              {releveRates.rolling?.excludedDays > 0 && ` (${releveRates.rolling.excludedDays} exclus)`}
            </Typography>
          </Box>
        </Box>
      </Grid>

      {/* Stats des heures */}
      <Grid item xs={12} md={8}>
        <Typography variant="subtitle1" gutterBottom>Heures par code</Typography>
        <Grid container spacing={1}>
          {hoursPerCode.codeDetails.map(({ code, hoursLogged, hoursExpected, periodicite, yearHoursLogged, yearHoursExpected }) => {
            const monthlyOverflow = periodicite !== 'variable' && hoursLogged > hoursExpected;
            const yearlyOverflow = (periodicite === 'annual' || periodicite === 'calendar') && 
              yearHoursExpected && yearHoursLogged > yearHoursExpected;
            const relevCode = releveCodes.find(rc => rc.code === code);
            const textColor = getTextColorForBackground(relevCode?.color);
            const hasMonthlyLimit = relevCode?.monthly_limit;
            const monthlyLimitExceeded = hasMonthlyLimit && hoursLogged > relevCode.monthly_limit;

            return (
              <Grid item xs={12} sm={6} md={4} key={code}>
                <Paper 
                  elevation={1}
                  sx={{ 
                    p: 1, 
                    backgroundColor: relevCode?.color || 'grey.100',
                    position: 'relative',
                    color: textColor,
                    '&:hover': {
                      transform: (monthlyOverflow || yearlyOverflow || monthlyLimitExceeded) ? 'scale(1.02)' : 'none',
                      transition: 'transform 0.2s'
                    }
                  }}
                >
                  {/* Alertes */}
                  {(monthlyOverflow || monthlyLimitExceeded) && (
                    <Tooltip title={monthlyLimitExceeded ? "Dépassement limite mensuelle" : "Dépassement dotation mensuelle"}>
                      <WarningIcon 
                        sx={{ 
                          position: 'absolute',
                          top: 4,
                          right: yearlyOverflow ? 28 : 4,
                          color: monthlyLimitExceeded ? 'error.main' : 'warning.main',
                          animation: 'pulse 2s infinite'
                        }}
                      />
                    </Tooltip>
                  )}
                  {yearlyOverflow && (
                    <Tooltip title="Dépassement dotation annuelle">
                      <ErrorIcon 
                        sx={{ 
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          color: 'error.main',
                          animation: 'pulse 2s infinite'
                        }}
                      />
                    </Tooltip>
                  )}

                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'inherit' }}>
                    {code}
                    {relevCode?.role ? ` (${relevCode.role})` : ''}
                  </Typography>
                  {/* N'afficher la dotation que si ce n'est pas un code variable */}
                  {periodicite !== 'variable' ? (
                    <Typography variant="body2" sx={{ 
                      color: monthlyOverflow ? 'warning.main' : 'inherit',
                      fontWeight: monthlyOverflow ? 'bold' : 'normal'
                    }}>
                      {formatDuration(hoursLogged)} / {formatDuration(hoursExpected)}
                    </Typography>
                  ) : (
                    <Typography variant="body2">
                      {formatDuration(hoursLogged)}
                    </Typography>
                  )}
                  {/* Afficher la limite mensuelle uniquement si elle existe et que ce n'est pas un code variable */}
                  {relevCode?.monthly_limit && periodicite !== 'variable' && (
                    <Typography variant="caption" sx={{ 
                      display: 'block',
                      color: monthlyLimitExceeded ? 'error.main' : 'text.secondary'
                    }}>
                      Maximum: {formatDuration(relevCode.monthly_limit)}
                    </Typography>
                  )}
                  {(periodicite === 'annual' || periodicite === 'calendar') && yearHoursExpected && (
                    <Typography variant="caption" sx={{ 
                      display: 'block',
                      color: yearlyOverflow ? 'error.main' : 'inherit',
                      fontWeight: yearlyOverflow ? 'bold' : 'normal'
                    }}>
                      {periodicite === 'calendar' ? 'Année calendaire' : 'Annuel'}: {formatDuration(yearHoursLogged)} / {formatDuration(yearHoursExpected)}
                    </Typography>
                  )}
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default PointageStats;
